import clsx from 'clsx';
import pluralize from 'pluralize';
import React, { useState } from 'react';

import FlagIcon from 'images/flag.svg';
import ShareIcon from 'images/share.svg';

import FlexLink from '@components/FlexLink';
import IconComment from '@components/Images/Rebranding/IconComment';
import { useAppContext } from '@contexts/AppContext';
import {
  useCommentActions,
  useCurrentUser,
  useEventTracking,
  useForumPostPinnedToggle,
  useServerSideRendering,
} from '@hooks';
import { edit_community_comment_path, edit_forum_post_path, forum_post_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { ForumPost } from '@utils/backend-api/forumPosts';

import LikesCount from '../LikesCount';
import ShareModal from '../ShareModal';
import SuperPinModal from '../SuperPinModal';
import styles from './styles.module.scss';

export default function ForumPostActions({
  forumPost,
  minimal,
  onDownloadScreenshot,
}: {
  forumPost: ForumPost;
  minimal?: boolean;
  onDownloadScreenshot?: (darkMode?: boolean) => void;
}) {
  const hydrated = useServerSideRendering();
  const { env } = useAppContext();
  const { currentUser } = useCurrentUser();
  const { comment } = forumPost;
  const [isShareModalOpen, setIsSharedModalOpen] = useState(false);
  const [isSuperPinModalOpen, setIsSuperPinModalOpen] = useState(false);
  const { mutate: toggleForumPostPinned, isLoading: isPinLoading } = useForumPostPinnedToggle();
  const { report, toggleLike, liked, likesCount, areLikesLoading } = useCommentActions(comment);
  const { trackEvent, Track } = useEventTracking({
    location: TrackingLocation.Post,
  });

  const commentsCount = forumPost.total_comments_count;
  const isAdmin = currentUser?.administrator?.is_active;

  const onTogglePinClick = () => {
    hydrated &&
      window.confirm(
        `Are you sure you want to ${forumPost.pinned_until ? 'unpin' : 'pin'} this post${
          forumPost.pinned_until ? '' : ' for the next 24 hours'
        }?`
      ) &&
      toggleForumPostPinned(forumPost.id);
    trackEvent(click({ clickTarget: ClickTarget.Pin }));
  };

  const handleLikeClick = () => {
    const clickType =
      !areLikesLoading && !liked ? ClickTarget.CommentLike : ClickTarget.CommentUnlike;
    trackEvent(click({ clickTarget: clickType, contentId: forumPost.id }));
  };

  const handleCommentJumpClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.CommentPost }));
  };

  const handleReportClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.CommentReport, contentId: forumPost.id }));
  };

  const handleManageClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.CommentManage, contentId: forumPost.id }));
  };

  return (
    <Track>
      <div
        className={clsx(styles.actions, {
          [styles.commentActionsMinimal]: minimal,
        })}
      >
        <div className={styles.actionContainer}>
          <button
            onClick={toggleLike}
            disabled={
              !!(areLikesLoading || env.READ_ONLY_MODE || !currentUser?.permitted_to_comment)
            }
            title="Like this post"
            className={clsx(styles.likeButton, styles.action, 'unstyled')}
            type="button"
            onClickCapture={handleLikeClick}
          >
            <span
              className={clsx({
                [styles.likesCountLiked]: liked,
              })}
            >
              <LikesCount count={likesCount} commentId={comment.id} />
            </span>
            {!minimal && (
              <span
                className={clsx(styles.smallAction, {
                  [styles.likesCountLiked]: liked,
                })}
              >
                {pluralize('like', likesCount === 0 ? 1 : likesCount)}
              </span>
            )}
          </button>
        </div>
        {minimal ? (
          <div className={styles.actionContainer}>
            <FlexLink
              href={forum_post_path(forumPost.id, { anchor: 'comments' })}
              className={styles.action}
              onClickCapture={handleCommentJumpClick}
            >
              <IconComment className="w-16" />
              {commentsCount}
              <span className={styles.smallAction}>{pluralize('Comment', commentsCount)}</span>
            </FlexLink>
          </div>
        ) : (
          <>
            {commentsCount > 0 && (
              <div className={styles.actionContainer}>
                <div className={styles.action}>
                  <IconComment className="w-16" />
                  {commentsCount}
                  <span className={styles.smallAction}>{pluralize('Comment', commentsCount)}</span>
                </div>
              </div>
            )}
          </>
        )}

        {!minimal && (
          <>
            <div className={styles.actionContainer}>
              <button
                className={clsx('unstyled', styles.action)}
                onClick={() => {
                  setIsSharedModalOpen(true);
                  trackEvent(click({ clickTarget: ClickTarget.ShareButton }));
                }}
              >
                <img src={ShareIcon} alt="Share" />
                <span className={styles.smallAction}>Share</span>
              </button>
            </div>
            <div className={styles.actionContainer}>
              <button
                onClick={report}
                onClickCapture={handleReportClick}
                className={clsx(styles.action, 'unstyled')}
                type="button"
              >
                <img src={FlagIcon} alt="Report" />
                <span className={styles.smallAction}>Report</span>
              </button>
            </div>
            {isAdmin && (
              <>
                <div className={styles.actionContainer}>
                  <a href={edit_community_comment_path(comment.id)} className={styles.action}>
                    Hide
                  </a>
                </div>
                <div className={styles.actionContainer}>
                  <button
                    onClick={onTogglePinClick}
                    className={styles.action}
                    disabled={isPinLoading}
                    type="button"
                  >
                    {forumPost.pinned_until ? 'Unpin' : 'Pin'}
                  </button>
                </div>
                <div className={styles.actionContainer}>
                  <button
                    onClick={() => {
                      setIsSuperPinModalOpen(true);
                      trackEvent(
                        click({ clickTarget: ClickTarget.SuperPin, contentId: forumPost.id })
                      );
                    }}
                    className={styles.action}
                    disabled={forumPost.super_pinned || !forumPost.email_digest_enabled}
                    type="button"
                  >
                    {forumPost.super_pinned ? 'Super Pinned' : 'Super Pin'}
                  </button>
                </div>
                <div className={styles.actionContainer}>
                  <FlexLink
                    href={edit_forum_post_path(forumPost.id)}
                    className={styles.action}
                    onClickCapture={handleManageClick}
                  >
                    Manage
                  </FlexLink>
                </div>
              </>
            )}
            {!!onDownloadScreenshot && (
              <div className={styles.actionContainer}>
                <button
                  onClick={() => {
                    onDownloadScreenshot();
                    trackEvent(
                      click({ clickTarget: ClickTarget.DownloadButton, contentId: forumPost.id })
                    );
                  }}
                  className={clsx(styles.action, 'unstyled')}
                  title="Download as shareable screenshot essay"
                  type="button"
                >
                  Download
                </button>
              </div>
            )}
          </>
        )}

        <ShareModal
          forumPost={forumPost}
          isOpen={isShareModalOpen}
          setIsOpen={setIsSharedModalOpen}
          onDownloadScreenshot={onDownloadScreenshot}
        />
        <SuperPinModal
          forumPostId={forumPost.id}
          isOpen={isSuperPinModalOpen}
          setIsOpen={setIsSuperPinModalOpen}
        />
      </div>
    </Track>
  );
}
